import React, { useContext, useState } from 'react';
import { SButton, SIcon, SSpinner } from '@chargebee/sting-react';
import { useHistory, useParams } from '@bbkShared/react-router';
import PlayContextProvider, {
  PlayContext,
} from '@bbkAdminComponents/play/play-context';
import { useUrl } from '@bbkAdminUtils/nav';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import {
  DRAWER_ANIMATION_DURATION,
  getRouteTypeFromPlayType,
} from '@bbkAdminComponents/unified-plays/utils/play-type-utils';
import { PlayNameAndBadges } from '@bbkAdminComponents/unified-plays/components/play-name-and-badges';
import { EditorDrawer } from '@bbkAdminComponents/editor-page-overlay/drawer';
import { OfferPresentation } from '@bbkAdminUtils/api-client/rest-offers-api';
import PlayPricingTableReports from '@bbkAdminComponents/unified-plays/reporting/reports/play-pricing-table-reports';
import PlayOfferReports from '@bbkAdminComponents/unified-plays/reporting/reports/play-offer-reports';
import CancelPageReports from '@bbkAdminComponents/unified-plays/reporting/reports/cancel-page-reports';

// TODO: Extract header to a separate component
const UnifiedReportingDashboardInner: React.FC = () => {
  // hooks
  const history = useHistory();
  const createUrl = useUrl();
  const [isOpen, setIsOpen] = useState<boolean>(true);

  const { play } = useContext(PlayContext);
  const { playId } = useParams<{ playId: string }>();

  // variables
  const isLoading = play?.id === undefined;
  const presentation = play?.presentation;

  // methods
  const handleClose = () => {
    if (!play) return;

    setIsOpen(false);
    const routeType = getRouteTypeFromPlayType(play.play_type);
    let returnPath: string;

    switch (routeType) {
      case 'acquisition':
        returnPath = AuthorizedPaths.unifiedPlaysAcquisition;
        break;
      case 'retention':
        returnPath = AuthorizedPaths.unifiedPlaysRetention;
        break;
      case 'expansion':
      default:
        returnPath = AuthorizedPaths.unifiedPlaysExpansion;
        break;
    }

    setTimeout(() => {
      history.push(createUrl(returnPath));
    }, DRAWER_ANIMATION_DURATION);
  };

  const renderReportingData = () => {
    if (presentation === OfferPresentation.pricing_page)
      return <PlayPricingTableReports playId={playId} />;
    if (
      presentation === OfferPresentation.modal ||
      presentation === OfferPresentation.pop_up ||
      presentation === OfferPresentation.banner
    )
      return <PlayOfferReports playId={playId} />;
    return <CancelPageReports playId={playId} />;
  };

  return (
    <EditorDrawer
      id="base-component"
      open={isOpen}
      onClose={handleClose}
      disableEnforceFocus
      className="tw-bg-pricing-editor-neutral-200"
      data-theme="light"
    >
      <div className="tw-flex tw-h-full !tw-w-full !tw-max-w-full tw-flex-col">
        {isLoading ? (
          <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-bg-cbrneutral-50">
            <SSpinner
              size="medium"
              testId="default-spinner"
              className="!tw-text-white"
            />
          </div>
        ) : (
          <>
            <div className="tw-flex tw-w-full tw-items-center tw-justify-between tw-border-b tw-border-neutral-100 tw-px-6 tw-py-4">
              <PlayNameAndBadges
                play={play}
                leftAction={
                  <SButton
                    aria-label="Close"
                    size="small"
                    variant="neutral-ghost"
                    className="tw-self-start"
                    onClick={handleClose}
                  >
                    <SIcon name="x" size={16} />
                  </SButton>
                }
                showActivationDate
              />
              <div className="tw-flex tw-flex-wrap tw-gap-2">
                <SButton
                  icon={<SIcon name="circle-question-mark" size={14} />}
                  iconPosition="left"
                  variant="neutral-ghost"
                >
                  Need help?
                </SButton>
                <SButton
                  icon={<SIcon name="pencil" size={14} />}
                  iconPosition="left"
                  variant="primary-outline"
                  onClick={() => {
                    history.push(
                      createUrl(AuthorizedPaths.unifiedPlaysEdit, { playId })
                    );
                  }}
                >
                  View Play Builder
                </SButton>
              </div>
            </div>
            <div className="tw-flex tw-w-full tw-flex-1 tw-items-start tw-justify-center tw-overflow-auto tw-bg-cbrneutral-50 tw-pt-12">
              <div className="tw-flex tw-w-[90%] tw-flex-col">
                {renderReportingData()}
              </div>
            </div>
          </>
        )}
      </div>
    </EditorDrawer>
  );
};

const UnifiedReportingDashboard: React.FC = () => (
  <PlayContextProvider>
    <UnifiedReportingDashboardInner />
  </PlayContextProvider>
);

export default UnifiedReportingDashboard;
